import {DrupalMedia} from "next-drupal";
import {DrupalVideoMedia} from "../../types/hygiena-types";
import {MutableRefObject, useState} from "react";
import {MediaImage} from "../templates/media/media--image";
import Image from "next/image";
import {absoluteURL} from "../../lib/absolute-url";
import {ImageIcon} from "../atoms/icon";
import classNames from "classnames";
import {PRODUCT_LARGE_IMAGE_STYLE, PRODUCT_TEASER_IMAGE_STYLE, PRODUCT_ZOOM_IMAGE_STYLE} from "../../types/image-styles";
import {THUMBNAIL_IMAGE_STYLE} from "../../types/image-styles";
import InnerImageZoom from "react-inner-image-zoom";
import dynamic from "next/dynamic";

const ReactPlayer = dynamic(() => import("react-player/vimeo"), {ssr: false});

interface ProductImagesProps {
  image_field: DrupalMedia[],
  video_field?: DrupalVideoMedia,
  openModal?: Function|null,
  openModalContent?: Function|null,
  isModal?: boolean,
  playVideo?: boolean,
  videoRef?: MutableRefObject<HTMLDivElement|null>,
  selectedImage?: DrupalMedia,
  selectedVideo?: DrupalVideoMedia,
  lazy?: boolean,
}

/**
 * Simply displays the ProductImages element inside the modal with some tweaked settings.
 *
 * @param image_field
 *   The image field from the product node.
 * @param video_field
 *   An optional video field from the product node.
 * @param playVideo
 *   Whether the video is to be played.
 * @param videoRef
 *   The ref to the first video element.
 * @param selectedImage
 *   The selected image.
 * @param selectedVideo
 *   The selected video.
 * @constructor
 */
export function ProductImagesModal({image_field, video_field, playVideo, videoRef, selectedImage, selectedVideo}: ProductImagesProps) {
  // Simply display the same images and thumbnail scroller, in a larger container with better resolution.
  return (
    <ProductImages image_field={image_field} video_field={video_field} isModal={true} playVideo={playVideo} selectedImage={selectedImage} selectedVideo={selectedVideo}/>
  )
}

/**
 * Product image and video, selector with lightbox component for product and sub-products.
 *
 * @param image_field
 *   The image field from the product node.
 * @param video_field
 *   An optional video field from the product node.
 * @param openModal
 *   Whether the modal is open or not.
 * @param openModalContent
 *   The content for the modal if open.
 * @param isModal
 *   Whether the images are in a modal state.
 * @param playVideo
 *   Whether the video is to be played.
 * @param videoRef
 *   The ref to the first video element.
 * @param selectedImage
 *   The selected image.
 * @param selectedVideo
 *   The selected video.
 * @constructor
 */
export default function ProductImages({image_field, video_field, openModal, openModalContent, isModal, playVideo, videoRef, selectedImage, selectedVideo, lazy}: ProductImagesProps) {
  // @todo handle responsive, perhaps no thumbs?
  const pageSize = 4;
  const combinedThumbs = combineThumbs() ?? [];
  const [activeVideo, setActiveVideo] = useState(getActiveVideo());
  const [activeImage, setActiveImage] = useState(getActiveImage());
  const [thumbs, setThumbs] = useState(combinedThumbs?.length ? combinedThumbs.slice(0, pageSize) : [] as DrupalMedia[]);
  const [play, setPlay] = useState(playVideo);
  const firstImage = image_field?.[0];
  const showThumbs = openModal || isModal;

  /**
   * Gets the active video.
   */
  function getActiveVideo() {
    if (selectedVideo) return selectedVideo;
    return video_field?.[0] ?? undefined;
  }

  /**
   * Gets the active image.
   */
  function getActiveImage() {
    if (selectedImage) return selectedImage;
    return !video_field?.[0] && image_field?.[0] ? image_field[0] : undefined;
  }

  /**
   * Combine the videos and images into thumbs.
   */
  function combineThumbs() {
    let videos = video_field?.length ? video_field : [];
    let images = image_field?.length ? image_field: [];
    return videos.concat(images);
  }

  /**
   * Sets the active image.
   *
   * @param key
   */
  function updateImage(key: number) {
    if (!image_field?.length) {
      return;
    }
    const image = thumbs.filter((item, idx) => key === idx);
    setActiveImage(image && image[0]);
  }

  /**
   * Move the thumbs forwards or backwards.
   *
   * @param direction
   */
  function updateThumbs(direction: -1|1) {
    let nextImage;
    let nextIdx;

    // Change the thumb to the next or previous image in the image_field array.
    for (let key in combinedThumbs) {
      let i = parseInt(key);
      const currentId = combinedThumbs?.[i]?.id;
      if (currentId === activeImage?.id || currentId === activeVideo?.id) {
        // Traverse backwards.
        if (direction === -1 && i - 1 >= 0) {
          nextIdx = i - 1;
          nextImage = combinedThumbs[nextIdx];
          if (combinedThumbs[nextIdx]?.field_media_oembed_video) {
            setActiveVideo(nextImage);
            setActiveImage(undefined);
            setPlay(isModal);
          }
          else {
            setActiveImage(nextImage);
            setActiveVideo(undefined);
            setPlay(false);
          }
          break;
        }
        else if (direction === 1 && i + 1 < combinedThumbs?.length) {
          nextIdx = i + 1;
          nextImage = combinedThumbs[nextIdx];
          if (combinedThumbs[nextIdx]?.field_media_oembed_video) {
            setActiveVideo(nextImage);
            setActiveImage(undefined);
            setPlay(isModal);
          }
          else {
            setActiveImage(nextImage);
            setActiveVideo(undefined);
            setPlay(false);
          }
          break;
        }
      }
    }
    // Change the thumbs slice if we've passed the existing thumbs.
    if (nextIdx !== undefined && nextImage) {
      const inThumbs = thumbs.filter(item => item?.id === nextImage?.id);
      if (!inThumbs.length) {
        // If we're going to end up with less thumbnails, slice the max from the end of the image field length.
        if (combinedThumbs.length - nextIdx < pageSize) {
          setThumbs(combinedThumbs.slice(combinedThumbs.length - pageSize, combinedThumbs.length));
        }
        else {
          setThumbs(combinedThumbs.slice(nextIdx, nextIdx + pageSize));
        }
      }
    }
  }

  /**
   * Cleans up the vimeo url for iframe embed.
   *
   * @param url
   */
  function getVimeoUrl(url: string) {
    // @todo flesh this out so we can account for more url variations.
    const vimeoUrl = url.replace("https://vimeo.com/", "//player.vimeo.com/video/");
    return vimeoUrl + "?autoplay=1&portrait=0&controls=1&responsive=1";
  }

  /**
   * Ensures that cms paths receive the correct image style path.
   *
   * @param url
   *   The url.
   * @param imageStyle
   *   The image style.
   */
  function getImageStyleUri(url: string, imageStyle: string): string {
    if (url.match(/^\//)) {
      return absoluteURL(`/sites/default/files/styles/${imageStyle}${url}`)
    }
    return url;
  }

  /**
   * Reveals the modal, if the playVideo parameter is true, it will autoplay the video.
   *
   * @param playVideo
   *   Play the video on modal open if true.
   * @param selectedImage
   *   The selected image.
   * @param selectedVideo
   *   The selected video.
   */
  function revealModal(playVideo:boolean = false, selectedImage?: DrupalMedia, selectedVideo?: DrupalVideoMedia) {
    if (openModal && openModalContent) {
      openModal(true);
      openModalContent(ProductImagesModal({image_field: image_field, video_field: video_field, playVideo: playVideo, selectedImage: selectedImage, selectedVideo: selectedVideo}))
    }
  }

  function getWidth() {
    if (typeof window === undefined) return "800px";
    if (window.innerWidth < 600) return "300px";
    if (window.innerWidth < 1024) return "600px";
    if (window.innerWidth >= 1920) return "1000px";

    return "800px";
  }

  function getHeight() {
    if (typeof window === undefined) return "500px";
    if (window.innerHeight < 600) return "300px";
    if (window.innerWidth < 1024) return "400px";
    if (window.innerWidth >= 1920) return "700px";

    return "500px";
  }

  return (
    <>
      <div className={classNames("flex justify-center items-start flex-wrap w-full h-full", {"items-center": isModal})}>
        {!isModal && firstImage && (
          <>
            {(firstImage.type === "media--image" || firstImage.type === "media--brandfolder_image") && (
              <div className="w-full max-h-[90%] h-[80%]"
                   onClick={() => {
                     setActiveImage(firstImage);
                     setActiveVideo(undefined);
                     revealModal(false, firstImage, undefined);
                   }}
              >
                <MediaImage
                  media={firstImage}
                  key={firstImage.id}
                  imageStyle={PRODUCT_TEASER_IMAGE_STYLE}
                  priority={!lazy}
                  loading={lazy ? "lazy" : "eager"}
                  style={{width: "auto", objectFit: "contain"}}
                  sizes={classNames(
                    {"400px": !isModal},
                    {"800px": isModal}
                  )}
                  className={classNames(
                    "mx-auto mb-6 flex justify-center relative w-auto",
                    {"cursor-pointer": openModal !== undefined},
                    {"h-72 max-w-sm": !isModal},
                    {"h-full mb-0": isModal}
                  )}
                />
              </div>
            )}
          </>
        )}
        {(isModal && !play && activeImage) && (
          <>
            {(activeImage.type === "media--image" || activeImage.type === "media--brandfolder_image") && (
              <>
              <div className="w-full max-h-[90%] h-[80%] text-center product-modal-image max-md:overflow-auto"
                onClick={() => {
                  setActiveVideo(undefined);
                  revealModal(false, selectedImage, undefined);
                }}
              >
                {activeImage?.image && (
                  <InnerImageZoom className="iiz--product-image" zoomPreload={true} src={getImageStyleUri(activeImage?.image?.image_style_uri?.[PRODUCT_LARGE_IMAGE_STYLE], PRODUCT_LARGE_IMAGE_STYLE)} zoomSrc={getImageStyleUri(activeImage?.image?.image_style_uri?.[PRODUCT_ZOOM_IMAGE_STYLE], PRODUCT_ZOOM_IMAGE_STYLE)}/>
                )}
                {activeImage?.bf_image && (
                  <InnerImageZoom className="iiz--product-image" zoomPreload={true} src={activeImage?.bf_image?.image_style_uri?.[PRODUCT_LARGE_IMAGE_STYLE]} zoomSrc={activeImage?.bf_image?.image_style_uri?.[PRODUCT_ZOOM_IMAGE_STYLE]} />
                )}
              </div>
              {activeImage?.field_image_caption && (
                <div className="container mx-auto text-center">
                  {activeImage.field_image_caption}
                </div>
              )}
              </>
            )}
          </>
        )}
        {(isModal && activeVideo) && (
          <div className={classNames("mb-6 lg:h-[500px] w-auto max-h-[80%] self-end max-w-full", {"hidden": !play})}>
            <ReactPlayer key={`video-modal--${activeVideo?.id}`} width={getWidth()} height={getHeight()} url={getVimeoUrl(activeVideo?.field_media_oembed_video)} volume={1} playing={play} controls style={{maxHeight: "100%", maxWidth: "100%"}}/>
          </div>
        )}
        {showThumbs && (combinedThumbs?.length > 1) ? (
          <div className={classNames(
            "flex w-full gap-3 justify-center items-center self-end",
            {"h-[10%] max-h-[10%] flex-grow": isModal}
          )}>
            {combinedThumbs?.length > 4 && (
              <>
                {/* Prev Button */}
                <ImageIcon onClick={() => updateThumbs(-1)} type={"chevron-left"} color={"black"} height={27} width={27} alt={"Next image"} className={"mr-3 opacity-30 cursor-pointer"}/>
              </>
            )}
            {/* Display thumbnails */}
            {thumbs.map((item, key) => {
              // Videos
              if (item?.field_media_oembed_video && item?.thumbnail?.uri?.url) {
                return (
                  <div key={`product-images--${item.id}`} className={classNames(
                    "relative cursor-pointer",
                  )} ref={video_field?.[0]?.id === item?.id ? videoRef : undefined} onClick={()=> {
                    setActiveVideo(item);
                    setActiveImage(undefined);
                    if (!isModal) revealModal(true, undefined, item);
                    else setPlay(true);
                  }}>
                    <Image src={absoluteURL(item.thumbnail.uri.url)} key={item.id} alt={item?.title ?? ""} width="60"
                           className={classNames(
                             "border border-hygienaGray relative px-0.5 bg-white cursor-pointer h-[60px] w-[80px] flex items-center rounded-md",
                           )}
                           height="60" style={{objectFit: "cover"}}/>
                    <div className={classNames(
                      "h-full w-full bg-primary opacity-90 hover:opacity-70 absolute inset-0 rounded-md transition-all duration-500 flex items-center justify-center",
                      {"!border-gray-500 border rounded-md": (item.id === activeVideo?.id)}
                    )}>
                      <ImageIcon type={"play"} color={"white"} height={25} width={25} alt={"Play"}/>
                    </div>
                  </div>
                );
              }
              // Images.
              else if (item?.image || item?.bf_image) {
                return (
                  <MediaImage
                    media={item}
                    key={`product-images--${key}`}
                    priority={!lazy}
                    loading={lazy ? "lazy" : "eager"}
                    placeholder={"empty"}
                    imageStyle={THUMBNAIL_IMAGE_STYLE}
                    height="60"
                    width="80"
                    style={{objectFit: "contain", maxHeight: "100%"}}
                    className={classNames(
                      "border border-hygienaGray relative px-0.5 bg-white cursor-pointer h-[60px] w-[80px] flex items-center rounded-md",
                      {"!border-gray-500": item.id === activeImage?.id}
                    )}
                    sizes="80px, 60px"
                    onClick={() => {
                      updateImage(key);
                      setActiveImage(item);
                      setActiveVideo(undefined);
                      revealModal(false, item, undefined);
                      setPlay(false);
                    }}
                  />
                )
              }
            })}
            {combinedThumbs?.length > 4 && (
              <>
                {/* Next Button */}
                <ImageIcon onClick={() => updateThumbs(1)} type={"chevron-right"} color={"black"} height={27} width={27} alt={"Next image"} className={"mr-3 opacity-30 cursor-pointer"}/>
              </>
            )}
          </div>
        ) : <></>}
      </div>
    </>
  );
}
